import React, { useState, useEffect } from "react"
import {
  Container,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup, Label, Input,
} from "reactstrap"
import { connect } from "react-redux"
import { getLeaderboards } from "./actions"
import useRankingOperation from "./useRankingOperation"
import RankingHistory, { rankingStatus } from "./RankingHistory"
import "../../assets/scss/custom/pages/_rankingsManagement.scss"

const formatTime = value => {
  if (!value || !Number.isFinite(new Date(value).getTime()))
    return "Unavailable"
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Bucharest",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value))
}
const LeaderboardsManagement = ({
  fetchLeaderboards,
  data,
  loading,
  error,
  actorId,
}) => {
  const [modal, setModal] = useState(null)
  const closeModal = () => setModal(value => value ? { ...value, closed: true } : null)
  const [reason, setReason] = useState("")
  const operation = useRankingOperation({ actorId, onChange: fetchLeaderboards })
  const pending = operation.pending
  useEffect(() => { fetchLeaderboards() }, [fetchLeaderboards])
  useEffect(() => {
    if (!["calculating", "published", "pending"].includes(data?.operation?.status)) return
    const timer = setInterval(fetchLeaderboards, 8000)
    return () => clearInterval(timer)
  }, [data?.operation?.status, fetchLeaderboards])
  const submit = () => {
    if (operation.start({ ...modal.payload, reason: reason.trim() }, modal.kind)) closeModal()
  }
  const restoring = modal?.kind === "restore"
  const busy = pending || ["pending", "calculating", "published"].includes(data?.operation?.status)
  const publication = data?.publication
  const schedule = data?.schedule
  return (
    <div className="page-content rankings-management">
      <Container fluid>
        <div className="rankings-management__heading">
          <h4>Rankings Management</h4>
          <span className="text-muted">Dates and times: Europe/Bucharest</span>
        </div>
        {error ? (
          <Card>
            <CardBody>
              <div role="alert">Ranking information could not be loaded.</div>
              <Button className="mt-2" onClick={fetchLeaderboards}>
                Retry ranking information
              </Button>
            </CardBody>
          </Card>
        ) : loading || !data ? (
          <p role="status">Loading ranking information…</p>
        ) : (
          <>
            <Card>
              <CardBody className="rankings-management__summary">
                <section aria-label="Published ranking">
                  <h5>Published ranking</h5>
                  {publication.status === "confirmed" ? (
                    <>
                      <strong>
                        Week {publication.week} · {publication.year}
                      </strong>
                      <p>Calculated: {formatTime(publication.date)}</p>
                      {publication.restoredAt && <p>Restored: {formatTime(publication.restoredAt)}</p>}
                    </>
                  ) : (
                    <>
                      <strong>Publication not verified</strong>
                      <p className="text-muted mb-0">
                        The existing records do not confirm a complete ranking
                        run. The rankings remain available; their publication
                        date cannot be verified.
                      </p>
                    </>
                  )}
                </section>
                <section aria-label="Automatic calculation">
                  <h5>Automatic calculation</h5>
                  {schedule.status === "available" ? (
                    <>
                      <strong>{formatTime(schedule.nextRun)}</strong>
                      <p className="text-muted mb-0">
                        Next scheduled run · Romania time
                      </p>
                    </>
                  ) : (
                    <>
                      <strong>Next run unavailable</strong>
                      <p className="text-muted mb-0">
                        The scheduler could not be verified. Its existing
                        schedule has not been changed.
                      </p>
                    </>
                  )}
                </section>
              </CardBody>
            </Card>
            <div className="rankings-management__actions">
              <Card>
                <CardBody>
                  <h5>Recalculate rankings</h5>
                  <p>
                    Run the existing calculation for all sports, levels, singles
                    and doubles.
                  </p>
                  <Button
                    color="primary"
                    disabled={busy || !actorId}
                    onClick={() => {
                      setReason("")
                      setModal({ kind: "recalculate", payload: { expectedRevision: publication?.revision ?? 0 } })
                    }}
                  >
                    Recalculate rankings
                  </Button>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <h5>Restore previous week</h5>
                  <p>
                    Return to the latest complete, confirmed version from the
                    previous calendar week.
                  </p>
                  {data.restore?.target && <p>Week {data.restore.target.week} · {data.restore.target.year}<br />Calculated: {formatTime(data.restore.target.date)}</p>}
                  <Button color="primary" disabled={busy || !actorId || !data.restore?.available} onClick={() => {
                    setReason("")
                    setModal({ kind: "restore", target: { ...data.restore.target }, payload: {
                      targetVersionId: data.restore.target.versionId, expectedRevision: data.restore.expectedRevision,
                      expectedPublishedVersionId: data.restore.expectedPublishedVersionId,
                    } })
                  }}>Restore previous week</Button>
                  {!data.restore?.available && <p className="text-muted mt-2 mb-0">
                    {data.restore?.reason === "RESTORE_ALREADY_PUBLISHED" ? "This previous-week version is already published."
                      : data.restore?.reason === "RANKING_BUSY" ? "Wait for the current ranking operation to finish."
                      : "No verified version is available for restoration."}
                  </p>}
                </CardBody>
              </Card>
            </div>
            <Card>
              <CardBody>
                <h5>Action Logs</h5>
                <RankingHistory initial={data?.history} formatTime={formatTime} />
              </CardBody>
            </Card>
          </>
        )}
        {operation.status !== "idle" && <div className={operation.status === "failed" ? "alert alert-danger" : "alert alert-info"} role={operation.status === "failed" ? "alert" : "status"}>
          {operation.status === "completed" ? (operation.operation?.kind === "restore" ? "Previous-week ranking restored." : "Rankings published and synchronized.") : operation.status === "published" ? "Rankings published. Synchronization is still pending and will be retried automatically." : operation.message || "Waiting for the saved result. Queue acceptance does not confirm publication."}
          {["unknown", "published"].includes(operation.status) && <Button size="sm" className="ml-2" onClick={operation.retry}>Check and resume request</Button>}
        </div>}
        {!pending && ["pending", "calculating", "published"].includes(data?.operation?.status) && <p role="status">Another ranking operation: {rankingStatus(data.operation.status)}.</p>}
        <Modal
          isOpen={Boolean(modal && !modal.closed)}
          toggle={pending ? undefined : closeModal}
          labelledBy="rankings-action-title"
          centered
        >
          <ModalHeader
            id="rankings-action-title"
            toggle={pending ? undefined : closeModal}
          >
            {restoring ? "Restore previous week" : "Recalculate rankings"}
          </ModalHeader>
          <ModalBody>
            {restoring ? <>
              <p><strong>Week {modal.target.week} · {modal.target.year}</strong><br />Calculated: {formatTime(modal.target.date)}</p>
              <p>The published rankings will use this version’s levels, positions and points. Current account levels stay unchanged. Players absent from this version will have no ranking in it. The next calculation uses current account data.</p>
              <p>Entries, draws, results and payments are unaffected.</p>
              <details><summary>Version details</summary><div className="text-break">Target: {modal.payload.targetVersionId}</div><div className="text-break">Current: {modal.payload.expectedPublishedVersionId}</div></details>
            </> : <p>This recalculates all sports, levels, singles and doubles. A complete version is published before synchronization is confirmed.</p>}
            <FormGroup>
              {/* A recalculation's reason is optional (T6): Action Logs already record who, when and the result. A restore needs one. */}
              <Label for="ranking-reason">{restoring ? <>Reason <span aria-hidden="true">*</span></> : "Reason (optional)"}</Label>
              <Input id="ranking-reason" type="textarea" rows={3} value={reason} required={restoring} maxLength={2000} onChange={event => setReason(event.target.value)} disabled={pending} />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button disabled={pending} onClick={closeModal}>
              Cancel
            </Button>
            <Button color="primary" disabled={pending || (restoring && !reason.trim())} onClick={submit}>
              {pending ? "Requesting…" : restoring ? "Restore ranking" : "Recalculate"}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  )
}
export default connect(
  ({ leaderboards, session }) => ({
    actorId: session?.userDetails?.id || session?.userDetails?._id,
    data: leaderboards.leaderboards,
    loading: leaderboards.loading,
    error: leaderboards.error,
  }),
  { fetchLeaderboards: getLeaderboards }
)(LeaderboardsManagement)
